/**
 * AppConfig.js - Centralized application configuration
 *
 * This file contains all configuration values that might need to be changed
 * across environments or during development. It prevents hardcoding values
 * throughout the application.
 */

import spotlightService from '../services/SpotlightService';
import { getDspEnvConfig } from './dspEnv';

const dspEnvConfig = getDspEnvConfig();

/**
 * API Configuration
 */
export const API_CONFIG = {
  // Base URLs
  // No hardcoded fallback domain; an empty value is reported by validateConfig().
  BASE_URL: dspEnvConfig.baseUrl || process.env.REACT_APP_API_URL || '',
  // No CDN_URL here on purpose: the image base is supplied by the API
  // (/init/config -> image_cdn_url, see SpotlightService.primeImageCdnUrl) so
  // it stays correct per environment and across the DNS cutover. It must never
  // come from a build-time env var again.

  // Request timeouts
  REQUEST_TIMEOUT: 10000, // ms
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // ms
};

/**
 * Application Settings
 */
export const APP_CONFIG = {
  // Application metadata
  APP_NAME: 'BKFC',
  APP_VERSION: process.env.REACT_APP_VERSION || '1.0.0',
  SUPPORT_URL: 'https://bareknuckle.zendesk.com/hc/en-us',

  // Feature flags
  FEATURES: {
    LIVE_TV: true,
    SEARCH: true,
    WATCHLIST: true,
    SERIES_SUPPORT: true,
    HOVER_PREVIEWS: true,
  },

  // UI Settings
  UI: {
    HOVER_DELAY: 500, // ms before showing hover modal
    HIDE_DELAY: 300, // ms before hiding hover modal
    OVERLAY_TIMEOUT: 3000, // ms before hiding watch page overlay
    ANIMATION_DURATION: 300, // ms for transitions
  },

  // Cache settings
  CACHE: {
    DEFAULT_TTL: 5 * 60 * 1000, // 5 minutes
    IMAGE_TTL: 30 * 60 * 1000, // 30 minutes
    VIDEO_TTL: 2 * 60 * 1000, // 2 minutes
  },
};

/**
 * Environment Detection
 */
export const ENVIRONMENT = {
  IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
  IS_PRODUCTION: process.env.NODE_ENV === 'production',
  IS_TEST: process.env.NODE_ENV === 'test',
};

/**
 * Cache for app load data to avoid repeated API calls
 */
let appLoadCache = null;
let appLoadPromise = null;
let companyIdCache = null;
let cacheTimestamp = null;

// Cache TTL: 1 hour (company ID shouldn't change frequently)
const COMPANY_ID_CACHE_TTL = 60 * 60 * 1000;

/**
 * Check if cached company ID is still valid
 * @returns {boolean} True if cache is valid
 */
const isCacheValid = () => {
  if (!companyIdCache || !cacheTimestamp) {
    return false;
  }
  return Date.now() - cacheTimestamp < COMPANY_ID_CACHE_TTL;
};

/**
 * Get company ID from app load call with robust caching
 * @param {boolean} forceRefresh - Force refresh of cached value
 * @returns {Promise<string>} Company ID from app data
 */
export const getCompanyId = async (forceRefresh = false) => {
  try {
    // Return cached value if valid and not forcing refresh
    if (!forceRefresh && isCacheValid() && companyIdCache) {
      return companyIdCache;
    }

    // If already fetching, wait for existing promise
    if (appLoadPromise) {
      const appData = await appLoadPromise;
      const companyId = appData?.app_data?.company_id;
      if (companyId) {
        companyIdCache = companyId;
        cacheTimestamp = Date.now();
        return companyId;
      }
    }

    // Fetch app load data
    appLoadPromise = spotlightService.getAppLoad();
    const appData = await appLoadPromise;

    // Cache the result
    appLoadCache = appData;
    appLoadPromise = null;

    const companyId = appData?.app_data?.company_id;

    if (!companyId) {
      companyIdCache = '6419d68d33c59c85e30cdc62'; // Fallback for safety
      cacheTimestamp = Date.now();
      return companyIdCache;
    }

    // Cache the company ID with timestamp
    companyIdCache = companyId;
    cacheTimestamp = Date.now();

    return companyId;
  } catch (error) {
    appLoadPromise = null; // Clear failed promise

    // Return cached value if available, even if expired
    if (companyIdCache) {
      return companyIdCache;
    }

    // Return fallback company ID if no cache available
    const fallbackId = '6419d68d33c59c85e30cdc62';
    companyIdCache = fallbackId;
    cacheTimestamp = Date.now();
    return fallbackId;
  }
};

/**
 * Helper function to validate required environment variables
 * @returns {Object} Validation results
 */
export const validateConfig = () => {
  const errors = [];
  const warnings = [];

  // Check required environment variables
  if (!API_CONFIG.BASE_URL) {
    errors.push('REACT_APP_API_URL is required');
  }

  if (!dspEnvConfig.apiKey) {
    errors.push('A DSP API key is required for app load');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Clear app load cache (useful for testing or when switching environments)
 */
export const clearAppLoadCache = () => {
  appLoadCache = null;
  appLoadPromise = null;
  companyIdCache = null;
  cacheTimestamp = null;
};

/**
 * Get cache status for debugging
 * @returns {Object} Cache status information
 */
export const getCacheStatus = () => {
  return {
    hasCompanyIdCache: !!companyIdCache,
    companyIdValue: companyIdCache,
    cacheAge: cacheTimestamp ? Date.now() - cacheTimestamp : null,
    isValid: isCacheValid(),
    ttl: COMPANY_ID_CACHE_TTL,
  };
};

// Validate configuration on import in development
if (ENVIRONMENT.IS_DEVELOPMENT) {
  const validation = validateConfig();
  if (!validation.isValid) {
    console.error('❌ Configuration errors:', validation.errors);
  }
  if (validation.warnings.length > 0) {
    console.warn('⚠️ Configuration warnings:', validation.warnings);
  }
}

export default {
  API_CONFIG,
  APP_CONFIG,
  ENVIRONMENT,
  getCompanyId,
  validateConfig,
  clearAppLoadCache,
  getCacheStatus,
};
